import { createClient } from '@supabase/supabase-js';
import {
  getCaliforniaDistricts,
  getCompetitiveCaliforniaDistricts,
  isCaliforniaDistrict
} from './californiaData';

/**
 * ============================================================================
 * CALIFORNIA DATA EXCEPTION - READ THIS
 * ============================================================================
 *
 * California candidate data is stored in the `candidates` table, NOT in
 * `all_candidates_2026` like other states.
 *
 * DATA STRUCTURE:
 * - `candidates` table: Contains all California congressional candidates
 * - `representatives` table: Links candidates to districts via `representative_id`
 * - Join: candidates.representative_id -> representatives.id
 * - District: representatives.district_number gives the district (e.g., 17 -> CA-17)
 *
 * Use fetchCaliforniaCandidates() or fetchCaliforniaCandidatesForDistrict()
 * to get California candidates from the database.
 * ============================================================================
 */

/**
 * WORKAROUND: Map district codes to handle database inconsistencies
 * GeoJSON files use: AK-0, DE-0
 * Database uses: AK-AL, DE-AL
 * This function converts the GeoJSON naming to database naming
 */
function normalizeDistrictCodeForDB(districtCode) {
  const codeMap = {
    'AK-0': 'AK-AL',
    'DE-0': 'DE-AL'
  };
  return codeMap[districtCode] || districtCode;
}

/**
 * Get all possible district code variants for querying candidates
 * (database has both AK-0 and AK-AL, DE-0 and DE-AL)
 */
function getAllDistrictCodeVariants(districtCodes) {
  const allCodes = [...districtCodes];

  districtCodes.forEach(code => {
    if (code === 'AK-AL' || code === 'AK-0') {
      if (!allCodes.includes('AK-AL')) allCodes.push('AK-AL');
      if (!allCodes.includes('AK-0')) allCodes.push('AK-0');
    }
    if (code === 'DE-AL' || code === 'DE-0') {
      if (!allCodes.includes('DE-AL')) allCodes.push('DE-AL');
      if (!allCodes.includes('DE-0')) allCodes.push('DE-0');
    }
  });

  return allCodes;
}

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://eoifovdmuuqdcospamru.supabase.co';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVvaWZvdmRtdXVxZGNvc3BhbXJ1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjAyOTI4NTAsImV4cCI6MjA3NTg2ODg1MH0.0aQ1JGJzAPqWanzYjR719mB9eKWNfhp3XD9ssQiZnOg';

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  realtime: {
    params: {
      eventsPerSecond: 2 // Rate limit realtime
    }
  }
});

// === CALIFORNIA CANDIDATE FETCHING FROM DATABASE ===

/**
 * Fetch all California congressional candidates from the `candidates` table
 * Joins with `representatives` to get district info
 * @returns {Promise<Object>} Map of district_code -> candidates array
 */
async function fetchCaliforniaCandidates() {
  try {
    const { data, error } = await supabase
      .from('candidates')
      .select(`
        id,
        name,
        party,
        is_incumbent,
        photo_url,
        website,
        phone,
        email,
        biography,
        ballot_designation,
        occupation,
        social_media,
        fec_candidate_id,
        representative_id,
        representatives!inner (
          id,
          district_number,
          office,
          is_active
        )
      `)
      .eq('representatives.office', 'U.S. Representative')
      .eq('representatives.is_active', true);

    if (error) {
      console.warn('Error fetching California candidates:', error);
      return {};
    }

    // Group by district code with deduplication
    const candidatesByDistrict = {};
    const seenCandidates = new Set(); // Track by id to prevent duplicates

    (data || []).forEach(c => {
      // Skip if we've already seen this candidate
      if (seenCandidates.has(c.id)) return;
      seenCandidates.add(c.id);

      const districtNumber = c.representatives?.district_number;
      if (districtNumber) {
        const districtCode = `CA-${districtNumber}`;
        if (!candidatesByDistrict[districtCode]) {
          candidatesByDistrict[districtCode] = [];
        }
        // Transform to match expected candidate format
        candidatesByDistrict[districtCode].push({
          id: c.id,
          full_name: c.name,
          name: c.name,
          party: c.party,
          is_incumbent: c.is_incumbent,
          photo_url: c.photo_url,
          website: c.website,
          phone: c.phone,
          email: c.email,
          biography: c.biography,
          ballot_designation: c.ballot_designation,
          occupation: c.occupation,
          social_media: c.social_media,
          fec_candidate_id: c.fec_candidate_id,
          district_code: districtCode
        });
      }
    });

    console.log(`✅ Fetched California candidates for ${Object.keys(candidatesByDistrict).length} districts (${seenCandidates.size} unique candidates)`);
    return candidatesByDistrict;
  } catch (error) {
    console.error('Error in fetchCaliforniaCandidates:', error);
    return {};
  }
}

/**
 * Fetch California candidates for a specific district
 * @param {string} districtCode - District code (e.g., "CA-17")
 * @returns {Promise<Array>} Array of candidates
 */
async function fetchCaliforniaCandidatesForDistrict(districtCode) {
  if (!isCaliforniaDistrict(districtCode)) return [];

  const districtNumber = parseInt(districtCode.replace('CA-', ''), 10);
  if (isNaN(districtNumber)) return [];

  try {
    const { data, error } = await supabase
      .from('candidates')
      .select(`
        id,
        name,
        party,
        is_incumbent,
        photo_url,
        website,
        phone,
        email,
        biography,
        ballot_designation,
        occupation,
        social_media,
        fec_candidate_id,
        representative_id,
        representatives!inner (
          id,
          district_number,
          office,
          is_active
        )
      `)
      .eq('representatives.office', 'U.S. Representative')
      .eq('representatives.is_active', true)
      .eq('representatives.district_number', districtNumber);

    if (error) {
      console.warn(`Error fetching candidates for ${districtCode}:`, error);
      return [];
    }

    // Transform to match expected candidate format with deduplication
    const seenIds = new Set();
    return (data || [])
      .filter(c => {
        if (seenIds.has(c.id)) return false;
        seenIds.add(c.id);
        return true;
      })
      .map(c => ({
        id: c.id,
        full_name: c.name,
        name: c.name,
        party: c.party,
        is_incumbent: c.is_incumbent,
        photo_url: c.photo_url,
        website: c.website,
        phone: c.phone,
        email: c.email,
        biography: c.biography,
        ballot_designation: c.ballot_designation,
        occupation: c.occupation,
        social_media: c.social_media,
        fec_candidate_id: c.fec_candidate_id,
        district_code: districtCode
      }));
  } catch (error) {
    console.error(`Error fetching candidates for ${districtCode}:`, error);
    return [];
  }
}

// === CALIFORNIA DATA MERGE UTILITIES ===

/**
 * Merge California fallback data with database results
 * Database data takes precedence - fallback fills gaps
 * NOTE: Candidates are fetched separately from the `candidates` table
 */
function mergeCaliforniaDistricts(dbDistricts, options = {}) {
  const { includeCA = true, filterFn = null } = options;
  if (!includeCA) return dbDistricts;

  // Get existing CA district codes from database
  const existingCACodes = new Set(
    dbDistricts
      .filter(d => d.district_code?.startsWith('CA-'))
      .map(d => d.district_code)
  );

  // Get California fallback data (district-level only)
  let caDistricts = getCaliforniaDistricts();

  // Apply filter if provided (for competitive districts, etc.)
  if (filterFn) {
    caDistricts = caDistricts.filter(filterFn);
  }

  // Only add CA districts that don't exist in database
  const missingCADistricts = caDistricts.filter(
    d => !existingCACodes.has(d.district_code)
  );

  // Merge and sort by district code
  // NOTE: Candidates will be added later via fetchCaliforniaCandidates
  const merged = [...dbDistricts, ...missingCADistricts];
  return merged.sort((a, b) =>
    (a.district_code || '').localeCompare(b.district_code || '')
  );
}

// === HELPER FUNCTIONS ===

export async function getCompetitiveDistricts() {
  const competitiveRatings = ['Lean D', 'Toss-Up', 'Lean R', 'Likely D', 'Likely R'];

  // First get competitive districts from database
  const { data: districts, error: districtError } = await supabase
    .from('districts')
    .select('*')
    .in('model_rating', competitiveRatings)
    .order('margin_2024', { ascending: true });

  if (districtError) throw districtError;

  // Merge California fallback for competitive districts
  const dbDistricts = districts || [];
  const mergedDistricts = mergeCaliforniaDistricts(dbDistricts, {
    filterFn: d => competitiveRatings.includes(d.model_rating)
  });

  if (mergedDistricts.length === 0) return [];

  // Get candidates from all_candidates_2026 for non-CA districts
  const nonCADistrictCodes = dbDistricts
    .filter(d => !d.district_code?.startsWith('CA-'))
    .map(d => d.district_code);

  let candidatesByDistrict = {};

  if (nonCADistrictCodes.length > 0) {
    // Include all district code variants (AK-0/AK-AL, DE-0/DE-AL)
    const allCodesToQuery = getAllDistrictCodeVariants(nonCADistrictCodes);

    const { data: candidates, error: candidatesError } = await supabase
      .from('all_candidates_2026')
      .select('*')
      .in('district_code', allCodesToQuery);

    if (candidatesError) {
      console.warn('Error fetching candidates:', candidatesError);
    }

    (candidates || []).forEach(c => {
      // Normalize district code for grouping (AK-AL -> AK-AL, AK-0 -> AK-AL, etc.)
      let normalizedCode = c.district_code;
      if (c.district_code === 'AK-0') normalizedCode = 'AK-AL';
      if (c.district_code === 'DE-0') normalizedCode = 'DE-AL';

      if (!candidatesByDistrict[normalizedCode]) {
        candidatesByDistrict[normalizedCode] = [];
      }
      candidatesByDistrict[normalizedCode].push(c);
    });
  }

  // Fetch California candidates from the candidates table
  const caCandidates = await fetchCaliforniaCandidates();

  // Return merged districts with candidates
  return mergedDistricts.map(d => ({
    ...d,
    candidates: isCaliforniaDistrict(d.district_code)
      ? (caCandidates[d.district_code] || [])
      : (candidatesByDistrict[d.district_code] || [])
  }));
}

export async function getAllDistricts(filters = {}) {
  try {
    // Build district query
    let query = supabase.from('districts').select('*');

    if (filters.ratings && filters.ratings.length > 0) {
      query = query.in('model_rating', filters.ratings);
    }

    if (filters.states && filters.states.length > 0) {
      query = query.in('state', filters.states);
    }

    if (filters.openSeats === true) {
      query = query.eq('is_open_seat', true);
    }

    // PERFORMANCE: Add pagination support
    const limit = filters.limit || 435;
    const offset = filters.offset || 0;

    if (limit !== 435) {
      query = query.range(offset, offset + limit - 1);
    }

    const { data: districts, error: districtError } = await query.order('district_code');

    if (districtError) throw districtError;

    const dbDistricts = districts || [];

    // Determine if we should include California fallback
    // Only include if: no state filter, or CA is in the state filter
    const shouldIncludeCA = !filters.states || filters.states.length === 0 ||
      filters.states.includes('CA');

    // Build filter function for California fallback based on current filters
    let caFilterFn = null;
    if (filters.ratings && filters.ratings.length > 0) {
      caFilterFn = d => filters.ratings.includes(d.model_rating);
    }
    if (filters.openSeats === true) {
      const prevFilter = caFilterFn;
      caFilterFn = d => (prevFilter ? prevFilter(d) : true) && d.is_open_seat === true;
    }

    // Merge California fallback data
    const mergedDistricts = mergeCaliforniaDistricts(dbDistricts, {
      includeCA: shouldIncludeCA,
      filterFn: caFilterFn
    });

    if (mergedDistricts.length === 0) return [];

    // Fetch candidates from all_candidates_2026 for non-CA districts only
    const nonCADistrictCodes = dbDistricts
      .filter(d => !d.district_code?.startsWith('CA-'))
      .map(d => d.district_code);

    let candidatesByDistrict = {};

    if (nonCADistrictCodes.length > 0) {
      const { data: candidates, error: candidatesError } = await supabase
        .from('all_candidates_2026')
        .select('*')
        .in('district_code', nonCADistrictCodes);

      if (candidatesError) {
        console.warn('Error fetching candidates:', candidatesError);
      }

      (candidates || []).forEach(c => {
        if (!candidatesByDistrict[c.district_code]) {
          candidatesByDistrict[c.district_code] = [];
        }
        candidatesByDistrict[c.district_code].push(c);
      });
    }

    // Fetch California candidates from the candidates table
    const caCandidates = shouldIncludeCA ? await fetchCaliforniaCandidates() : {};

    // Return merged districts with candidates
    return mergedDistricts.map(d => ({
      ...d,
      candidates: isCaliforniaDistrict(d.district_code)
        ? (caCandidates[d.district_code] || [])
        : (candidatesByDistrict[d.district_code] || [])
    }));
  } catch (error) {
    console.error('Error loading districts:', error);
    return [];
  }
}

export async function getDistrictDetail(districtCode) {
  // Normalize district code for database query (handles AK-0 -> AK-AL, DE-0 -> DE-AL)
  const dbDistrictCode = normalizeDistrictCodeForDB(districtCode);

  // First try to get district data from database
  const { data: district, error: districtError } = await supabase
    .from('districts')
    .select(`
      *,
      district_demographics (*)
    `)
    .eq('district_code', dbDistrictCode)
    .single();

  // If database doesn't have this district and it's California, use fallback
  if (districtError && isCaliforniaDistrict(districtCode)) {
    const caDistricts = getCaliforniaDistricts();
    const caDistrict = caDistricts.find(d => d.district_code === districtCode);

    if (caDistrict) {
      // Fetch candidates from database for this CA district
      const candidates = await fetchCaliforniaCandidatesForDistrict(districtCode);
      return {
        ...caDistrict,
        district_demographics: [], // No demographics in fallback
        candidates
      };
    }
  }

  if (districtError) throw districtError;

  // For California districts, get candidates from the candidates table
  if (isCaliforniaDistrict(districtCode)) {
    const candidates = await fetchCaliforniaCandidatesForDistrict(districtCode);
    return {
      ...district,
      candidates
    };
  }

  // For non-CA districts, get candidates from all_candidates_2026
  // Handle both district code formats (e.g., both AK-0 and AK-AL exist in database)
  const codesToQuery = [dbDistrictCode];
  if (districtCode === 'AK-0') codesToQuery.push('AK-AL');
  if (districtCode === 'DE-0') codesToQuery.push('DE-AL');

  const { data: candidates, error: candidatesError } = await supabase
    .from('all_candidates_2026')
    .select('*')
    .in('district_code', codesToQuery)
    .order('is_incumbent', { ascending: false });

  if (candidatesError) {
    console.warn('Error fetching candidates:', candidatesError);
  }

  return {
    ...district,
    district_code: districtCode, // Use original district code for display
    candidates: candidates || []
  };
}

export async function getDistrictGeometry(districtCode) {
  const { data, error } = await supabase
    .from('district_geometries')
    .select('simplified_geometry, bbox_north, bbox_south, bbox_east, bbox_west')
    .eq('district_code', districtCode)
    .single();

  if (error) throw error;
  return data;
}

export async function getAllDistrictsWithGeometries() {
  try {
    // PERFORMANCE WARNING: This loads ALL geometry data (~50MB+)
    // For production, consider lazy loading or using a map tile service
    console.time('Loading district geometries');

    const { data, error } = await supabase
      .from('districts')
      .select(`
        id,
        district_code,
        name,
        state,
        model_rating,
        margin_2024,
        district_geometries (
          simplified_geometry,
          bbox_north,
          bbox_south,
          bbox_east,
          bbox_west
        )
      `)
      .not('district_geometries', 'is', null)
      .order('district_code');

    console.timeEnd('Loading district geometries');

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error loading district geometries:', error);
    return [];
  }
}

// OPTIMIZED: Load geometry data on-demand for a single district
export async function getDistrictGeometryById(districtCode) {
  try {
    const { data, error } = await supabase
      .from('district_geometries')
      .select('simplified_geometry, bbox_north, bbox_south, bbox_east, bbox_west')
      .eq('district_code', districtCode)
      .single();

    if (error) {
      console.warn(`No geometry found for ${districtCode}`);
      return null;
    }
    return data;
  } catch (error) {
    console.error(`Error loading geometry for ${districtCode}:`, error);
    return null;
  }
}

export async function searchAll(query) {
  if (!query) return [];

  const searchTerm = `%${query}%`;
  const queryLower = query.toLowerCase();

  // Search districts in database
  const { data: districts, error: districtError } = await supabase
    .from('districts')
    .select('*')
    .or(`district_code.ilike.${searchTerm},name.ilike.${searchTerm},incumbent_name.ilike.${searchTerm}`)
    .limit(20);

  if (districtError) throw districtError;

  const dbDistricts = districts || [];

  // Also search California fallback data
  const caFallbackDistricts = getCaliforniaDistricts().filter(d => {
    // Check if this CA district is already in DB results
    const existsInDb = dbDistricts.some(db => db.district_code === d.district_code);
    if (existsInDb) return false;

    // Match against district code, name, or incumbent name
    return (
      d.district_code?.toLowerCase().includes(queryLower) ||
      d.name?.toLowerCase().includes(queryLower) ||
      d.incumbent_name?.toLowerCase().includes(queryLower)
    );
  });

  // Merge results
  const allDistricts = [...dbDistricts, ...caFallbackDistricts].slice(0, 20);

  if (allDistricts.length === 0) return [];

  // Fetch candidates for non-CA DB districts
  const nonCADistrictCodes = dbDistricts
    .filter(d => !d.district_code?.startsWith('CA-'))
    .map(d => d.district_code);

  let candidatesByDistrict = {};

  if (nonCADistrictCodes.length > 0) {
    const { data: candidates, error: candidatesError } = await supabase
      .from('all_candidates_2026')
      .select('*')
      .in('district_code', nonCADistrictCodes);

    if (candidatesError) {
      console.warn('Error fetching candidates for search:', candidatesError);
    }

    (candidates || []).forEach(c => {
      // Normalize district code for grouping (AK-AL -> AK-AL, AK-0 -> AK-AL, etc.)
      let normalizedCode = c.district_code;
      if (c.district_code === 'AK-0') normalizedCode = 'AK-AL';
      if (c.district_code === 'DE-0') normalizedCode = 'DE-AL';

      if (!candidatesByDistrict[normalizedCode]) {
        candidatesByDistrict[normalizedCode] = [];
      }
      candidatesByDistrict[normalizedCode].push(c);
    });
  }

  // Fetch California candidates
  const caCandidates = await fetchCaliforniaCandidates();

  return allDistricts.map(d => ({
    ...d,
    candidates: isCaliforniaDistrict(d.district_code)
      ? (caCandidates[d.district_code] || [])
      : (candidatesByDistrict[d.district_code] || [])
  }));
}

export async function getStats() {
  try {
    // Get database districts
    const { data, error } = await supabase
      .from('districts')
      .select('district_code, model_rating, is_open_seat, incumbent_party');

    if (error) throw error;

    const dbDistricts = data || [];

    // Merge California fallback data
    const allDistricts = mergeCaliforniaDistricts(dbDistricts);

    if (allDistricts.length === 0) {
      return {
        total: 435,
        majority: 218,
        competitive: 0,
        openSeats: 0,
        demSeats: 213,
        repSeats: 222
      };
    }

    // Calculate all stats in one pass from merged data
    const stats = allDistricts.reduce((acc, district) => {
      // Count competitive districts (Lean and Toss-Up only)
      if (['Lean D', 'Toss-Up', 'Lean R'].includes(district.model_rating)) {
        acc.competitive++;
      }
      // Count open seats
      if (district.is_open_seat === true) {
        acc.openSeats++;
      }
      // Count by party
      if (district.incumbent_party === 'Democratic') {
        acc.demSeats++;
      } else if (district.incumbent_party === 'Republican') {
        acc.repSeats++;
      }
      return acc;
    }, { competitive: 0, openSeats: 0, demSeats: 0, repSeats: 0 });

    return {
      total: 435, // Always 435 US House seats
      majority: 218,
      competitive: stats.competitive,
      openSeats: stats.openSeats,
      demSeats: stats.demSeats || 213,
      repSeats: stats.repSeats || 222,
      dataCompleteness: Math.round((allDistricts.length / 435) * 100)
    };
  } catch (error) {
    console.error('Error loading stats:', error);
    return {
      total: 435,
      majority: 218,
      competitive: 0,
      openSeats: 0,
      demSeats: 213,
      repSeats: 222
    };
  }
}
